import { PropsWithChildren, useEffect, useState } from "react";
import { Platform } from "react-native";
import { LoggedOnAppUser } from "./LoggedOnAppUser";
import { useFirebase } from "../firebase/useFirebase";
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, User, onAuthStateChanged, GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithCredential, signInWithRedirect, getRedirectResult } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { AuthContext } from "./AuthContext";
import { CustomAlert } from "@/components/ThemedAlert";
import { FirebaseServices } from "@/services/firebase/config";
import { sendVerificationEmail, sendPasswordReset } from "@/services/firebase/email-auth";
import { getAppConfig } from "@/services/globals";
import Constants from "expo-constants";
import { setAnalyticsUser, clearAnalyticsUser } from "@/services/analytics";
import { initializeEmulatorOnFirstLogin } from "@/services/firebase/emulator-setup";
import { callFunction } from "@/services/firebase/helpers";

export function AuthProvider({ children }: PropsWithChildren) {
  const InitTimeMs = 5000;

  const [isInitialized, setIsInitialized] = useState(false);
  const [user, setUser] = useState<LoggedOnAppUser | undefined>(undefined);

  const services: FirebaseServices | undefined = useFirebase();

  //TODO: const [[isLoading, session], setSession] = useStorageState("session"); //TODO: Use session for something!

  const onAuthState = async (user: User | null): Promise<LoggedOnAppUser | null> => {
    console.log("onAuthState", user);

    if (user !== null) {
      // In LOCAL emulator mode, initialize the emulator database on first login
      await initializeEmulatorOnFirstLogin().catch((err) => {
        console.warn("[AuthProvider] Error running initializeEmulatorFunc:", err);
      });

      // Use cached token result for fast startup
      let tokenResult = await user.getIdTokenResult();

      // If userUid or essential role claims (user, viewer) are missing, sync with backend to ensure they are populated
      const isMissingClaims =
        !tokenResult.claims.userUid ||
        !tokenResult.claims.user ||
        !tokenResult.claims.viewer;

      if (isMissingClaims && services) {
        try {
          const syncRes: any = await callFunction(services, "syncUserClaimsFunc");
          if (syncRes?.userUid || syncRes?.data?.userUid || syncRes?.status) {
            tokenResult = await user.getIdTokenResult(true);
          }
        } catch (err) {
          console.warn("[AuthProvider] callFunction syncUserClaimsFunc error, attempting fallback:", err);
          if (services.functions) {
            try {
              const syncFunc = httpsCallable(services.functions, "syncUserClaimsFunc");
              const syncRes: any = await syncFunc();
              if (syncRes?.data?.userUid || syncRes?.data?.status) {
                tokenResult = await user.getIdTokenResult(true);
              }
            } catch (fallbackErr) {
              console.warn("[AuthProvider] Failed fallback to sync userUid claim:", fallbackErr);
            }
          }
        }
      }

      const appUser = await mapUser(user);
      appUser.claims = tokenResult.claims;
      const resolvedUserId = (tokenResult.claims.userUid as string) || appUser.userId || user.uid;
      appUser.userId = resolvedUserId;
      appUser.authUid = user.uid;

      setUser(appUser);
      setAnalyticsUser(appUser.userId, {
        email: appUser.email || "",
        displayName: appUser.displayName || "",
      });
      setIsInitialized(true);
      return appUser;
    } else {
      setUser(undefined);
      clearAnalyticsUser();
      setIsInitialized(true);
      return null;
    }
  };

  useEffect(() => {
    if (Platform.OS === "web" && services?.auth) {
      getRedirectResult(services.auth)
        .then((result) => {
          if (result?.user) {
            console.log("[AuthProvider] Redirect sign-in result retrieved:", result.user.email);
            onAuthState(result.user);
          }
        })
        .catch((err) => {
          console.warn("[AuthProvider] getRedirectResult error:", err);
        });
    }
  }, [services?.auth]);

  useEffect(() => {
    console.log("useEffect", services);
    if (services === undefined) return;

    const unsubscribe = onAuthStateChanged(services.auth, onAuthState); //TODO
    return unsubscribe;

    //TODO: Does this auto unsub?
    // return () => unsubscribe(); // unsubscribing from the listener when the component is unmounting.
  }, [services]); // Don't add more dependancies!!! This must only be called on start!

  // Set a timer so we can set Initialized after 5s
  useEffect(() => {
    console.log("Set auth timer");
    const timer = setTimeout(() => {
      console.log("Auth timer - initialised");
      setIsInitialized(true);
    }, InitTimeMs);

    return () => {
      console.log("Auth timer - cleared");
      clearTimeout(timer);
    };
  }, []); // has no dependency - this will be called on-component-mount

  async function mapUser(user: User): Promise<LoggedOnAppUser> {
    const appUser = user as any as LoggedOnAppUser;
    if (user.photoURL) {
      appUser.photoUrl = user.photoURL;
    }
    appUser.userId = user.uid;
    return appUser;
  }

  async function signUp(email: string, password: string): Promise<LoggedOnAppUser | null> {
    console.info("Signing up", email);

    if (services === undefined) throw new Error("signUp-Firebase not initialized");

    // Perform sign-in logic here
    const creds = await createUserWithEmailAndPassword(services.auth, email, password);

    //TODO: Only login once onAuthStateChanged
    const appUser = mapUser(creds.user);

    return appUser;
  }

  async function signIn(email: string, password: string): Promise<LoggedOnAppUser | null> {
    console.info("Signing in", email, services?.auth);

    if (services === undefined) throw new Error("signIn-Firebase not initialized!");

    // Perform sign-in logic here
    try {
      const creds = await signInWithEmailAndPassword(services.auth, email, password);
      console.log("creds", creds);

      //TODO: Only login once onAuthStateChanged
      const appUser = mapUser(creds.user);

      return appUser;
    } catch (Err) {
      throw Err;
    }
  }

  async function signInWithGoogle(): Promise<LoggedOnAppUser | null> {
    //JJ - When we deploy web we do not have all these environment variables - so instead we add them to the
    // Expo config in app.config.ts.
    // So we can use them here...
    const vars = Constants.expoConfig?.extra!;

    console.info("Signing in with Google");
    if (services === undefined) throw new Error("signInWithGoogle-Firebase not initialized");

    try {
      if (Platform.OS === "web") {
        const provider = new GoogleAuthProvider();
        provider.setCustomParameters({ prompt: "select_account" });
        console.warn("BEFORE Creds");
        let creds;
        try {
          creds = await signInWithPopup(services.auth, provider);
        } catch (popupErr: any) {
          if (popupErr?.code === "auth/popup-blocked") {
            console.warn("[AuthProvider] Popup blocked by browser; falling back to signInWithRedirect");
            await signInWithRedirect(services.auth, provider);
            return null;
          }
          throw popupErr;
        }
        console.warn("Creds", creds);
        const appUser = await onAuthState(creds.user);
        return appUser;
      } else {
        console.warn("BEFORE googlesignin");
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { GoogleSignin } = require("@react-native-google-signin/google-signin");
        const webClientId = vars?.GOOGLE_WEB_CLIENT_ID || process.env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID;
        if (!webClientId) {
          console.warn("[AuthProvider] GOOGLE_WEB_CLIENT_ID is not defined in config or env.");
        }
        GoogleSignin.configure({
          webClientId,
        });
        await GoogleSignin.hasPlayServices();
        const userInfo = await GoogleSignin.signIn();
        if (userInfo?.type === "cancelled") {
          console.info("[AuthProvider] Google sign-in was cancelled by the user.");
          return null;
        }
        const idToken = userInfo.data?.idToken || userInfo.idToken;
        if (!idToken) {
          throw new Error("No Google ID Token returned");
        }
        const credential = GoogleAuthProvider.credential(idToken);
        const creds = await signInWithCredential(services.auth, credential);
        const appUser = await onAuthState(creds.user);
        return appUser;
      }
    } catch (err: any) {
      if (
        err?.code === "auth/popup-closed-by-user" ||
        err?.code === "auth/cancelled-popup-request" ||
        err?.code === "12501" ||
        String(err?.message || "").toLowerCase().includes("cancelled")
      ) {
        console.info("[AuthProvider] Google sign-in was cancelled by the user.");
        return null;
      }
      if (err?.code === "10" || String(err?.code).includes("DEVELOPER_ERROR")) {
        console.warn(
          "signInWithGoogle DEVELOPER_ERROR (code 10): Ensure Android SHA-1 fingerprint and Web Client ID are registered in Firebase Console.",
          err
        );
      } else {
        console.error("signInWithGoogle Error", err);
      }
      throw err;
    }
  }

  async function signInWithMicrosoft(): Promise<LoggedOnAppUser | null> {
    console.info("Signing in with Microsoft");
    if (services === undefined) throw new Error("Firebase not initialized");

    try {
      const provider = new OAuthProvider("microsoft.com");
      provider.setCustomParameters({ prompt: "select_account" });
      const creds = await signInWithPopup(services.auth, provider);
      const appUser = await mapUser(creds.user);
      return appUser;
    } catch (err) {
      throw err;
    }
  }

  async function emailVerification() {
    const firebaseUser = user as any as User; // TODO: Confirm its the full user

    console.log("emailVerification", firebaseUser);

    const config = getAppConfig();

    await sendVerificationEmail(config.domain, firebaseUser)
      .then((result) => {
        console.log("sendVerificationEmail", result);

        // Email sent!
        CustomAlert.alert("Alert", "A verification link has been sent to your email. If you don't see the verification email in a few minutes, please check your spam or junk folder.", [
          {
            text: "Ok",
            onPress: () => {
              //navigation.replace("AccountLoginRegister");
            },
          },
        ]);
      })
      .catch((err) => {
        console.log("sendVerificationEmail", err);
      });
  }

  async function signOut() {
    console.info("Signing out");

    if (services === undefined) throw new Error("Firebase not initialized");

    // Perform sign-out logic here
    await services.auth.signOut();

    //setSession(null);
    setUser(undefined);
  }

  async function sendPasswordResetEmail() {
    if (services === undefined) throw new Error("Firebase not initialized");
    if (!user || !user.email) throw new Error("No user logged in");

    console.log("sendPasswordResetEmail", user.email);

    const config = getAppConfig();

    try {
      await sendPasswordReset(config.domain, services.auth, user.email);
      CustomAlert.alert("Success", `A password reset link has been sent to ${user.email}.`);
    } catch (err: any) {
      console.error("sendPasswordResetEmail error", err);
      CustomAlert.alert("Error", err.message || "Failed to send password reset link.");
    }
  }

  async function refreshUser() {
    if (services?.auth.currentUser) {
      await services.auth.currentUser.reload();
      await onAuthState(services.auth.currentUser);
    }
  }

  return (
    <AuthContext
      value={{
        signUp: signUp,
        signIn: signIn,
        signInWithGoogle: signInWithGoogle,
        signInWithMicrosoft: signInWithMicrosoft,
        signOut: signOut,
        emailVerification: emailVerification,
        sendPasswordReset: sendPasswordResetEmail,
        refreshUser: refreshUser,
        user: user,
        isInitialized: isInitialized,
        isAuthenticated: user !== undefined,
      }}
    >
      {children}
    </AuthContext>
  );
}
